// ============================================================
// DerivacionesPage — Bandeja del psicólogo + "Mis derivaciones"
// del staff que las puede crear.
// ============================================================

import React, { useEffect, useState } from 'react';
import { I, UserAvatar } from 'components/ui';
import { useAuth } from 'contexts/AuthContext';
import { derivacionPsicologicaService } from 'services/derivacionPsicologicaService';
import { atencionPsicologicaService } from 'services/atencionPsicologicaService';
import BuscadorMatricula from 'components/psicologia/BuscadorMatricula';
import AtencionFormModal from 'components/psicologia/AtencionFormModal';
import {
  DerivacionResponse,
  DerivacionPayload,
  DerivacionEstadoPayload,
  PrioridadDerivacion,
  EstadoDerivacion,
  PRIORIDAD_LABEL,
  PRIORIDAD_COLOR,
  PRIORIDAD_RANK,
  ESTADO_DERIVACION_LABEL,
  ESTADO_DERIVACION_COLOR,
  AtencionPsicologicaResponse,
} from 'types/psicologia.types';
import { MatriculaResponse } from 'types/matricula.types';

const PRIORIDADES: PrioridadDerivacion[] = ['URGENTE', 'ALTA', 'MEDIA', 'BAJA'];
const ESTADOS:    EstadoDerivacion[]    = ['PENDIENTE', 'ACEPTADA', 'ATENDIDA', 'RECHAZADA'];

const Spinner: React.FC<{ size?: number }> = ({ size = 6 }) => (
  <svg className={`animate-spin w-${size} h-${size}`} viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z" />
  </svg>
);

const DerivacionesPage: React.FC = () => {
  const { user } = useAuth();
  const rol = user?.rol;
  const isPsicoStaff = rol === 'PSICOLOGO' || rol === 'ADMIN' || rol === 'DIRECTOR';

  type Tab = 'bandeja' | 'mias';
  const [tab, setTab] = useState<Tab>(isPsicoStaff ? 'bandeja' : 'mias');
  const [estadoFiltro, setEstadoFiltro] = useState<EstadoDerivacion>('PENDIENTE');
  const [items, setItems] = useState<DerivacionResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Modales
  const [showCrear, setShowCrear] = useState(false);
  const [accionPara, setAccionPara] = useState<{ d: DerivacionResponse; tipo: 'ACEPTAR' | 'RECHAZAR' | 'ATENDER' } | null>(null);

  const cargar = async () => {
    setLoading(true); setError(null);
    try {
      const data = tab === 'bandeja'
        ? await derivacionPsicologicaService.listar(estadoFiltro)
        : await derivacionPsicologicaService.listarMias();
      setItems(data);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al cargar derivaciones');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [tab, estadoFiltro]);

  // Ordenar bandeja por prioridad desc luego fecha asc
  const itemsOrdenados = React.useMemo(() => {
    const arr = [...items];
    if (tab === 'bandeja') {
      arr.sort((a, b) => {
        const r = PRIORIDAD_RANK[b.prioridad] - PRIORIDAD_RANK[a.prioridad];
        if (r !== 0) return r;
        return a.fechaRegistro.localeCompare(b.fechaRegistro);
      });
    } else {
      arr.sort((a, b) => b.fechaRegistro.localeCompare(a.fechaRegistro));
    }
    return arr;
  }, [items, tab]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Derivaciones psicológicas</h2>
          <p className="text-slate-400 text-sm mt-0.5">
            {isPsicoStaff
              ? 'Bandeja de casos derivados al área de psicología'
              : 'Casos que has derivado al área de psicología'}
          </p>
        </div>
        <button
          onClick={() => setShowCrear(true)}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-indigo-700 hover:bg-indigo-800 text-white text-sm font-bold"
        >
          <I.Plus size={14} /> Nueva derivación
        </button>
      </div>

      {/* Tabs (solo para staff de psicología) */}
      {isPsicoStaff && (
        <div className="flex gap-1 bg-slate-100 rounded-xl p-1 max-w-md">
          {([
            { key: 'bandeja', label: 'Bandeja' },
            { key: 'mias',    label: 'Mías'    },
          ] as const).map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all
                ${tab === t.key ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {/* Filtros de estado para la bandeja */}
      {tab === 'bandeja' && (
        <div className="flex flex-wrap gap-2">
          {ESTADOS.map(e => (
            <button
              key={e}
              onClick={() => setEstadoFiltro(e)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border transition-all
                ${estadoFiltro === e ? `${ESTADO_DERIVACION_COLOR[e]} shadow-sm` : 'bg-white text-slate-400 border-slate-200 hover:bg-slate-50'}`}
            >
              {ESTADO_DERIVACION_LABEL[e]}
            </button>
          ))}
        </div>
      )}

      {error && <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-600 text-sm">{error}</div>}

      {/* Lista */}
      {loading && items.length === 0 ? (
        <div className="flex justify-center py-16 text-indigo-700"><Spinner size={8} /></div>
      ) : itemsOrdenados.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm py-12 text-center">
          <p className="text-slate-400 text-sm">No hay derivaciones en este filtro</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {itemsOrdenados.map(d => (
            <DerivacionCard
              key={d.idDerivacion}
              d={d}
              isPsicoStaff={isPsicoStaff}
              onAccion={(tipo) => setAccionPara({ d, tipo })}
            />
          ))}
        </div>
      )}

      {showCrear && (
        <CrearDerivacionModal
          onClose={() => setShowCrear(false)}
          onCreated={(d) => { setItems(prev => [d, ...prev]); setShowCrear(false); }}
        />
      )}

      {accionPara && (
        <AccionDerivacionModal
          derivacion={accionPara.d}
          tipo={accionPara.tipo}
          onClose={() => setAccionPara(null)}
          onSaved={(actualizada) => {
            setItems(prev => prev.map(x => x.idDerivacion === actualizada.idDerivacion ? actualizada : x));
            setAccionPara(null);
          }}
        />
      )}
    </div>
  );
};

// ── Card de derivación ────────────────────────────────────────

const DerivacionCard: React.FC<{
  d: DerivacionResponse;
  isPsicoStaff: boolean;
  onAccion: (tipo: 'ACEPTAR' | 'RECHAZAR' | 'ATENDER') => void;
}> = ({ d, isPsicoStaff, onAccion }) => {
  const fechaCorta = d.fechaRegistro.slice(0, 16).replace('T', ' ');
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 flex flex-col sm:flex-row sm:items-start gap-4">
      <UserAvatar
        idUsuario={d.estudianteId}
        fotoPerfil={d.estudianteFotoPerfil}
        nombres={d.estudianteNombres}
        apellidos={d.estudianteApellidos}
        size={48}
        rounded="xl"
      />
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full border ${PRIORIDAD_COLOR[d.prioridad]}`}>
            {PRIORIDAD_LABEL[d.prioridad].toUpperCase()}
          </span>
          <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full border ${ESTADO_DERIVACION_COLOR[d.estado]}`}>
            {ESTADO_DERIVACION_LABEL[d.estado]}
          </span>
        </div>
        <p className="text-sm font-bold text-slate-800">
          {d.estudianteApellidos}, {d.estudianteNombres}
        </p>
        <p className="text-xs text-slate-500 mb-2">
          {d.gradoNombre} {d.seccionNombre} · {d.nivel} · DNI {d.estudianteDni}
        </p>
        <p className="text-sm text-slate-700 leading-snug whitespace-pre-line">{d.motivo}</p>
        <p className="text-[11px] text-slate-400 mt-2">
          Derivado por <span className="font-semibold text-slate-600">{d.derivadoPorApellidos}, {d.derivadoPorNombres}</span> ({d.derivadoPorRol}) · {fechaCorta}
        </p>
        {d.observacionesPsicologo && (
          <div className="mt-2 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 text-xs text-blue-800">
            <span className="font-bold">Nota psicólogo:</span> {d.observacionesPsicologo}
          </div>
        )}
      </div>

      {/* Acciones del psicólogo */}
      {isPsicoStaff && d.estado === 'PENDIENTE' && (
        <div className="flex flex-col gap-2 sm:flex-shrink-0">
          <button
            onClick={() => onAccion('ACEPTAR')}
            className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold"
          >
            Aceptar
          </button>
          <button
            onClick={() => onAccion('ATENDER')}
            className="px-3 py-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white text-xs font-bold"
          >
            Atender
          </button>
          <button
            onClick={() => onAccion('RECHAZAR')}
            className="px-3 py-1.5 rounded-lg bg-white border border-red-200 text-red-600 hover:bg-red-50 text-xs font-bold"
          >
            Rechazar
          </button>
        </div>
      )}
      {isPsicoStaff && d.estado === 'ACEPTADA' && (
        <div className="flex flex-col gap-2 sm:flex-shrink-0">
          <button
            onClick={() => onAccion('ATENDER')}
            className="px-3 py-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white text-xs font-bold"
          >
            Atender
          </button>
        </div>
      )}
    </div>
  );
};

// ── Modal: Crear derivación ───────────────────────────────────

const CrearDerivacionModal: React.FC<{
  onClose: () => void;
  onCreated: (d: DerivacionResponse) => void;
}> = ({ onClose, onCreated }) => {
  const [matricula, setMatricula] = useState<MatriculaResponse | null>(null);
  const [motivo, setMotivo] = useState('');
  const [prioridad, setPrioridad] = useState<PrioridadDerivacion>('MEDIA');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const enviar = async () => {
    if (!matricula) { setError('Selecciona un estudiante.'); return; }
    if (motivo.trim().length < 10) { setError('Describe el motivo con al menos 10 caracteres.'); return; }
    const payload: DerivacionPayload = {
      matriculaId: matricula.idMatricula,
      motivo:      motivo.trim(),
      prioridad,
    };
    setSubmitting(true); setError(null);
    try {
      const created = await derivacionPsicologicaService.crear(payload);
      onCreated(created);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al crear derivación');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="modal-head">
          <div>
            <h2>Nueva derivación</h2>
            <div className="sub">El área de psicología recibirá una alerta inmediata.</div>
          </div>
          <button onClick={onClose} className="sae-icon-btn"><I.X /></button>
        </div>
        <div className="modal-body grid gap-4">
          <div className="field">
            <label>Estudiante <span className="required">*</span></label>
            <BuscadorMatricula
              selected={matricula}
              onSelected={setMatricula}
              onClear={() => setMatricula(null)}
            />
          </div>
          <div className="field">
            <label>Prioridad</label>
            <select className="input" value={prioridad} onChange={e => setPrioridad(e.target.value as PrioridadDerivacion)}>
              {PRIORIDADES.map(p => <option key={p} value={p}>{PRIORIDAD_LABEL[p]}</option>)}
            </select>
          </div>
          <div className="field">
            <label>Motivo de la derivación <span className="required">*</span></label>
            <textarea
              className="input"
              rows={4}
              value={motivo}
              onChange={e => setMotivo(e.target.value)}
              placeholder="Describe los signos observados, contexto y por qué consideras necesaria la atención psicológica."
              maxLength={1000}
            />
            <p className="text-xs text-slate-400 mt-1">{motivo.length} / 1000</p>
          </div>
          {error && <div className="rounded-r-sm bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={onClose} disabled={submitting}>Cancelar</button>
          <button className="btn btn-primary" onClick={enviar} disabled={submitting}>
            {submitting ? 'Enviando…' : 'Enviar derivación'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Modal: Acción del psicólogo ───────────────────────────────

const AccionDerivacionModal: React.FC<{
  derivacion: DerivacionResponse;
  tipo: 'ACEPTAR' | 'RECHAZAR' | 'ATENDER';
  onClose: () => void;
  onSaved: (d: DerivacionResponse) => void;
}> = ({ derivacion, tipo, onClose, onSaved }) => {
  const [observaciones, setObservaciones] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Para ATENDER: necesitamos vincular una atención existente o crear una nueva
  const [showAtencionForm, setShowAtencionForm] = useState(false);
  const [atencionesExistentes, setAtencionesExistentes] = useState<AtencionPsicologicaResponse[]>([]);
  const [atencionVinculadaId, setAtencionVinculadaId] = useState<number | ''>('');
  const [loadingAtenciones, setLoadingAtenciones] = useState(false);

  useEffect(() => {
    if (tipo !== 'ATENDER') return;
    setLoadingAtenciones(true);
    atencionPsicologicaService.listarPorMatricula(derivacion.matriculaId)
      .then(setAtencionesExistentes)
      .catch(() => setAtencionesExistentes([]))
      .finally(() => setLoadingAtenciones(false));
  }, [tipo, derivacion.matriculaId]);

  const guardar = async () => {
    let payload: DerivacionEstadoPayload;
    if (tipo === 'ACEPTAR') {
      payload = { estado: 'ACEPTADA', observacionesPsicologo: observaciones.trim() || undefined };
    } else if (tipo === 'RECHAZAR') {
      if (observaciones.trim().length < 5) { setError('Indica el motivo del rechazo.'); return; }
      payload = { estado: 'RECHAZADA', observacionesPsicologo: observaciones.trim() };
    } else {
      if (!atencionVinculadaId) { setError('Selecciona o crea la sesión que atendió esta derivación.'); return; }
      payload = {
        estado:                'ATENDIDA',
        atencionVinculadaId:   Number(atencionVinculadaId),
        observacionesPsicologo: observaciones.trim() || undefined,
      };
    }
    setSubmitting(true); setError(null);
    try {
      const updated = await derivacionPsicologicaService.actualizarEstado(derivacion.idDerivacion, payload);
      onSaved(updated);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al actualizar');
    } finally {
      setSubmitting(false);
    }
  };

  const titulo = tipo === 'ACEPTAR' ? 'Aceptar derivación' : tipo === 'RECHAZAR' ? 'Rechazar derivación' : 'Atender derivación';

  // Construir matricula mínima para el modal de atención
  const matriculaMin: MatriculaResponse = {
    idMatricula:           derivacion.matriculaId,
    codigoMatricula:       null,
    fase:                  'REGULAR',
    estudianteId:          derivacion.estudianteId,
    estudianteNombres:     derivacion.estudianteNombres,
    estudianteApellidos:   derivacion.estudianteApellidos,
    estudianteDni:         derivacion.estudianteDni,
    estudianteCodigoSiagie:null,
    gradoId:               0, gradoNombre: derivacion.gradoNombre, gradoNivel: derivacion.nivel,
    seccionId:             0, seccionNombre: derivacion.seccionNombre,
    anioEscolarId:         0, anio: 0,
    fechaMatricula:        null, activo: true,
    pagoMatricula:         null, pensiones: null,
  };

  return (
    <>
      <div className="modal-backdrop" onClick={onClose}>
        <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
          <div className="modal-head">
            <div>
              <h2>{titulo}</h2>
              <div className="sub">{derivacion.estudianteApellidos}, {derivacion.estudianteNombres}</div>
            </div>
            <button onClick={onClose} className="sae-icon-btn"><I.X /></button>
          </div>
          <div className="modal-body grid gap-4">
            {tipo === 'ATENDER' && (
              <div className="field">
                <label>Sesión vinculada <span className="required">*</span></label>
                {loadingAtenciones ? (
                  <p className="text-xs text-slate-400">Cargando sesiones…</p>
                ) : (
                  <>
                    <select
                      className="input"
                      value={atencionVinculadaId}
                      onChange={e => setAtencionVinculadaId(e.target.value ? Number(e.target.value) : '')}
                    >
                      <option value="">— Selecciona una sesión —</option>
                      {atencionesExistentes.map(a => (
                        <option key={a.idAtencionPsicologica} value={a.idAtencionPsicologica}>
                          {a.fechaHora.slice(0, 10)} {a.fechaHora.slice(11, 16)} · {a.estado}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setShowAtencionForm(true)}
                      className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-indigo-700 hover:text-indigo-900"
                    >
                      <I.Plus size={12} /> Crear nueva sesión y vincularla
                    </button>
                  </>
                )}
              </div>
            )}

            <div className="field">
              <label>{tipo === 'RECHAZAR' ? 'Motivo del rechazo' : 'Observaciones (opcional)'}</label>
              <textarea
                className="input"
                rows={3}
                value={observaciones}
                onChange={e => setObservaciones(e.target.value)}
                maxLength={500}
                placeholder={tipo === 'RECHAZAR'
                  ? 'Explica por qué no procede esta derivación.'
                  : 'Notas internas (no visibles para quien derivó).'}
              />
            </div>
            {error && <div className="rounded-r-sm bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
          </div>
          <div className="modal-foot">
            <button className="btn btn-ghost" onClick={onClose} disabled={submitting}>Cancelar</button>
            <button className="btn btn-primary" onClick={guardar} disabled={submitting}>
              {submitting ? 'Guardando…' : titulo}
            </button>
          </div>
        </div>
      </div>

      {showAtencionForm && (
        <AtencionFormModal
          matriculaPreseleccionada={matriculaMin}
          onClose={() => setShowAtencionForm(false)}
          onSaved={(a) => {
            setAtencionesExistentes(prev => [a, ...prev]);
            setAtencionVinculadaId(a.idAtencionPsicologica);
            setShowAtencionForm(false);
          }}
        />
      )}
    </>
  );
};

export default DerivacionesPage;
